import { useState, useMemo } from "react";
import { useLang } from "@/i18n/LangContext";
import { formatWithKRW, getLocaleString } from "@/lib/format";
import type { HistoryPoint } from "@/types/api";

export default function PriceHistoryChart({ data, currentPrice, currency }: { data: HistoryPoint[]; currentPrice?: number; currency?: string }) {
  const { lang, t } = useLang();
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const sorted = useMemo(() =>
    [...data].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()),
    [data]
  );

  if (sorted.length === 0) return null;

  const prices = sorted.map((d) => d.price.amount);
  const allPrices = currentPrice ? [...prices, currentPrice] : prices;
  const minPrice = Math.min(...allPrices);
  const maxPrice = Math.max(...allPrices);
  const priceRange = maxPrice - minPrice || 1;

  const W = 700;
  const H = 280;
  const padL = 55;
  const padR = 30;
  const padT = 30;
  const padB = 50;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;

  const dates = sorted.map((d) => new Date(d.timestamp).getTime());
  const minDate = dates[0];
  const maxDate = currentPrice ? Date.now() : dates[dates.length - 1];
  const dateRange = maxDate - minDate || 1;

  const points = sorted.map((d, i) => ({
    x: padL + (chartW * (dates[i] - minDate)) / dateRange,
    y: padT + chartH - (chartH * (d.price.amount - minPrice)) / priceRange,
  }));

  // Add current price as the last point
  if (currentPrice !== undefined) {
    points.push({
      x: padL + chartW,
      y: padT + chartH - (chartH * (currentPrice - minPrice)) / priceRange,
    });
  }

  // Create step line path (price holds until next change)
  let pathD = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    pathD += ` L ${points[i].x} ${points[i - 1].y} L ${points[i].x} ${points[i].y}`;
  }

  // Area fill
  const areaD = pathD + ` L ${points[points.length - 1].x} ${padT + chartH} L ${points[0].x} ${padT + chartH} Z`;

  // Y-axis labels (5 ticks)
  const yTicks = Array.from({ length: 5 }, (_, i) => {
    const val = minPrice + (priceRange * i) / 4;
    const y = padT + chartH - (chartH * i) / 4;
    return { val, y };
  });

  // X-axis labels
  const xLabelCount = Math.min(sorted.length, 5);
  const xTicks = Array.from({ length: xLabelCount }, (_, i) => {
    const idx = Math.floor((i * (sorted.length - 1)) / Math.max(xLabelCount - 1, 1));
    return {
      x: points[idx].x,
      label: new Date(dates[idx]).toLocaleDateString(getLocaleString(lang), { year: "2-digit", month: "short" }),
    };
  });

  const dataWithCurrent = [
    ...sorted.map((d) => ({ ...d, isCurrent: false })),
    ...(currentPrice !== undefined
      ? [{ shop: { name: t.gameDetail.currentPrice }, price: { amount: currentPrice }, timestamp: new Date().toISOString(), cut: 0, isCurrent: true }]
      : []),
  ];

  return (
    <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm p-6 mb-6">
      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-8 h-8 rounded-lg bg-gray-900 dark:bg-card-border flex items-center justify-center">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
          </svg>
        </div>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t.gameDetail.priceHistory}</h2>
      </div>

      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[320px]" style={{ maxHeight: 320 }}>
          {/* Grid lines */}
          {yTicks.map((tick, i) => (
            <g key={i}>
              <line x1={padL} y1={tick.y} x2={W - padR} y2={tick.y} stroke="#f0f0f0" strokeWidth="1" />
              <text x={padL - 10} y={tick.y + 4} textAnchor="end" fill="#9ca3af" fontSize="11" fontWeight="500">
                {currency === "USD" || lang !== "ko"
                  ? `$${tick.val >= 100 ? Math.round(tick.val) : tick.val.toFixed(2)}`
                  : `\u20A9${tick.val >= 1000 ? `${(tick.val / 1000).toFixed(tick.val >= 10000 ? 0 : 1)}K` : Math.round(tick.val).toLocaleString(getLocaleString(lang))}`}
              </text>
            </g>
          ))}

          {/* X axis labels */}
          {xTicks.map((tick, i) => (
            <text key={i} x={tick.x} y={H - 10} textAnchor="middle" fill="#9ca3af" fontSize="11" fontWeight="500">
              {tick.label}
            </text>
          ))}

          {/* Area fill */}
          <defs>
            <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0.02" />
            </linearGradient>
          </defs>
          <path d={areaD} fill="url(#priceGradient)" />

          {/* Line */}
          <path d={pathD} fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinejoin="round" />

          {/* Data points */}
          {points.map((pt, i) => (
            <g key={i}>
              <circle
                cx={pt.x}
                cy={pt.y}
                r={hoveredIdx === i ? 6 : 4}
                fill={i === points.length - 1 && currentPrice !== undefined ? "#1f2937" : "#10b981"}
                stroke="white"
                strokeWidth="2"
                className="cursor-pointer transition-all"
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(null)}
              />
              {/* Hover tooltip */}
              {hoveredIdx === i && (
                <g>
                  <rect
                    x={pt.x - 75}
                    y={pt.y - 52}
                    width="150"
                    height="42"
                    rx="8"
                    fill="#1f2937"
                    opacity="0.95"
                  />
                  <text x={pt.x} y={pt.y - 33} textAnchor="middle" fill="white" fontSize="12" fontWeight="700">
                    {formatWithKRW(dataWithCurrent[i].price.amount, currency, lang)}
                  </text>
                  <text x={pt.x} y={pt.y - 18} textAnchor="middle" fill="#9ca3af" fontSize="10">
                    {dataWithCurrent[i].shop.name} · {new Date(dataWithCurrent[i].timestamp).toLocaleDateString(getLocaleString(lang))}
                  </text>
                </g>
              )}
            </g>
          ))}

          {/* Axes */}
          <line x1={padL} y1={padT} x2={padL} y2={padT + chartH} stroke="#e5e7eb" strokeWidth="1" />
          <line x1={padL} y1={padT + chartH} x2={W - padR} y2={padT + chartH} stroke="#e5e7eb" strokeWidth="1" />
        </svg>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-3 text-xs text-gray-500 dark:text-muted">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-emerald-500" />
          <span>{t.gameDetail.historicLowRecord}</span>
        </div>
        {currentPrice !== undefined && (
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-gray-800" />
            <span>{t.gameDetail.currentPrice}</span>
          </div>
        )}
        <span className="ml-auto text-gray-400 dark:text-muted">
          via IsThereAnyDeal
        </span>
      </div>
    </div>
  );
}
